import { ServiceAccountJsonUpload } from '@/components/admin/ServiceAccountJsonUpload';
import { VERTEX_REGIONS } from '@/components/admin/vertexRegions';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { ApiError, api } from '@/lib/api';
import {
  BLOCKED_MODEL_MESSAGE,
  type CreateLlmEndpointRequest,
  CreateLlmEndpointRequestSchema,
  type LlmEndpoint,
  type Provider,
  type ProviderConfig,
  ProviderSchema,
  type UpdateLlmEndpointRequest,
  UpdateLlmEndpointRequestSchema,
  isBlockedModel,
  isVertexProvider,
  providerSupportsExtendedThinking,
} from '@innermate/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from './Modal';

type Mode = { kind: 'create' } | { kind: 'edit'; endpoint: LlmEndpoint };

interface Props {
  open: boolean;
  mode: Mode | null;
  onClose: () => void;
}

/**
 * Provider 카탈로그.
 * - cloudHosted=true : SDK 가 자체 default baseUrl 을 가진 클라우드 provider.
 *   사용자가 baseUrl 을 비워두면 서버 어댑터에서 SDK 기본값으로 라우팅된다.
 *   defaultBaseUrl 은 "참고용" 으로만 자동 채워지지만, 비워도 동작한다.
 * - cloudHosted=false : 로컬 / self-hosted. baseUrl 명시 필수.
 */
const PROVIDER_OPTIONS: Array<{
  value: Provider;
  label: string;
  defaultBaseUrl: string;
  cloudHosted: boolean;
  /** probe 결과가 없을 때 모델 select 에 노출할 권장 모델. */
  recommendedModels?: string[];
}> = [
  {
    value: 'openai',
    label: 'OpenAI (ChatGPT)',
    defaultBaseUrl: 'https://api.openai.com/v1',
    cloudHosted: true,
    recommendedModels: ['gpt-5', 'gpt-5-mini', 'gpt-4o', 'gpt-4o-mini'],
  },
  {
    value: 'anthropic',
    label: 'Anthropic (Claude)',
    defaultBaseUrl: 'https://api.anthropic.com',
    cloudHosted: true,
    recommendedModels: [
      'claude-opus-4-7',
      'claude-sonnet-4-6',
      'claude-haiku-4-5',
      'claude-opus-4-5',
    ],
  },
  {
    value: 'gemini',
    label: 'Google Gemini',
    defaultBaseUrl: 'https://generativelanguage.googleapis.com',
    cloudHosted: true,
    // probe 가 동작하지 않는 잠깐의 fallback 용. 실제 모델 목록은 API key 입력 후
    // 자동 probe 가 동적으로 가져온다 (preview 포함).
    recommendedModels: [
      'gemini-3-pro-preview',
      'gemini-3-flash-preview',
      'gemini-2.5-pro',
      'gemini-2.5-flash',
      'gemini-2.5-flash-lite',
    ],
  },
  {
    // 드롭다운에는 'GCP Vertex AI' 하나만 노출되고, publisher(Gemini/Claude)는 폼 안에서
    // 선택한다 (gcp ↔ gcp-anthropic). gcp-anthropic 항목은 dropdown 에서 숨기되
    // providerMeta(recommendedModels/cloudHosted) 조회용으로 카탈로그엔 남겨둔다.
    value: 'gcp',
    label: 'GCP Vertex AI',
    defaultBaseUrl: '',
    cloudHosted: true,
    // Vertex AI 는 model 목록 API 가 publisher 별로 다양해 자동 probe 불가.
    // 권장 모델을 셀렉트로 안내.
    recommendedModels: [
      'gemini-2.0-flash',
      'gemini-1.5-pro',
      'gemini-1.5-flash',
      'gemini-1.5-flash-002',
    ],
  },
  {
    value: 'gcp-anthropic',
    label: 'GCP Vertex AI (Claude)',
    defaultBaseUrl: '',
    cloudHosted: true,
    // Vertex 의 Claude 도 list API 가 없어 권장 모델 셀렉트로 안내. GCP 문서 표기대로
    // bare ID(`@버전` 핀 생략). 정확한 ID 는 프로젝트 Model Garden 기준 — 직접 입력 가능.
    recommendedModels: ['claude-opus-4-8', 'claude-sonnet-4-6', 'claude-haiku-4-5'],
  },
  {
    value: 'vllm',
    label: 'vLLM',
    defaultBaseUrl: 'http://localhost:8000/v1',
    cloudHosted: false,
  },
  {
    value: 'mlx',
    label: 'mlx_lm.server',
    defaultBaseUrl: 'http://localhost:8183/v1',
    cloudHosted: false,
  },
  {
    value: 'ollama',
    label: 'Ollama',
    defaultBaseUrl: 'http://localhost:11434/v1',
    cloudHosted: false,
  },
  {
    value: 'lm-studio',
    label: 'LM Studio',
    defaultBaseUrl: 'http://localhost:1234/v1',
    cloudHosted: false,
  },
  {
    value: 'openai-compatible',
    label: 'OpenAI 호환 (기타)',
    defaultBaseUrl: '',
    cloudHosted: false,
  },
];

interface FormState {
  name: string;
  displayName: string;
  description: string;
  provider: Provider;
  baseUrl: string;
  apiKey: string;
  model: string;
  extraHeadersRaw: string;
  /** GCP Vertex AI 의 project id. provider !== 'gcp' 일 땐 무시. */
  gcpProjectId: string;
  /** GCP Vertex AI 의 region (location). provider !== 'gcp' 일 땐 무시. */
  gcpLocation: string;
  isDefault: boolean;
  isEnabledForUsers: boolean;
  extendedThinking: boolean;
  supportsVision: boolean;
  isVisionDefault: boolean;
  supportsAudio: boolean;
  isAudioDefault: boolean;
  supportsImageGeneration: boolean;
  isImageGenerationDefault: boolean;
  supportsEmbedding: boolean;
  embeddingDim: string;
  contextWindow: string;
  maxConcurrent: string;
  maxOutputTokens: string;
  sortOrder: string;
  relayAlias: string;
}

const EMPTY: FormState = {
  name: '',
  displayName: '',
  description: '',
  provider: 'openai-compatible',
  baseUrl: '',
  apiKey: '',
  model: '',
  extraHeadersRaw: '',
  gcpProjectId: '',
  gcpLocation: 'global',
  isDefault: false,
  isEnabledForUsers: true,
  extendedThinking: false,
  supportsVision: false,
  isVisionDefault: false,
  supportsAudio: false,
  isAudioDefault: false,
  supportsImageGeneration: false,
  isImageGenerationDefault: false,
  supportsEmbedding: false,
  embeddingDim: '',
  contextWindow: '',
  maxConcurrent: '10',
  maxOutputTokens: '',
  sortOrder: '0',
  relayAlias: '',
};

export function EndpointFormModal({ open, mode, onClose }: Props) {
  if (!open || !mode) return null;
  return mode.kind === 'create' ? (
    <FormBody onClose={onClose} kind="create" />
  ) : (
    <FormBody onClose={onClose} kind="edit" endpoint={mode.endpoint} />
  );
}

type FormBodyProps =
  | { onClose: () => void; kind: 'create' }
  | { onClose: () => void; kind: 'edit'; endpoint: LlmEndpoint };

function FormBody(props: FormBodyProps) {
  const qc = useQueryClient();
  const isEdit = props.kind === 'edit';
  const endpoint = isEdit ? props.endpoint : null;

  const initialState: FormState = useMemo(() => {
    if (!endpoint) return EMPTY;
    return {
      name: endpoint.name,
      displayName: endpoint.displayName ?? '',
      description: endpoint.description ?? '',
      provider: endpoint.provider,
      baseUrl: endpoint.baseUrl,
      apiKey: '',
      model: endpoint.model,
      extraHeadersRaw: Object.keys(endpoint.extraHeaders).length
        ? JSON.stringify(endpoint.extraHeaders, null, 2)
        : '',
      gcpProjectId: endpoint.providerConfig.projectId ?? '',
      gcpLocation: endpoint.providerConfig.location ?? '',
      isDefault: endpoint.isDefault,
      isEnabledForUsers: endpoint.isEnabledForUsers,
      extendedThinking: endpoint.extendedThinking,
      supportsVision: endpoint.supportsVision,
      isVisionDefault: endpoint.isVisionDefault,
      supportsAudio: endpoint.supportsAudio,
      isAudioDefault: endpoint.isAudioDefault,
      supportsImageGeneration: endpoint.supportsImageGeneration,
      isImageGenerationDefault: endpoint.isImageGenerationDefault,
      supportsEmbedding: endpoint.supportsEmbedding,
      embeddingDim: endpoint.embeddingDim ? String(endpoint.embeddingDim) : '',
      contextWindow: endpoint.contextWindow ? String(endpoint.contextWindow) : '',
      maxConcurrent: endpoint.maxConcurrent ? String(endpoint.maxConcurrent) : '',
      maxOutputTokens: endpoint.maxOutputTokens ? String(endpoint.maxOutputTokens) : '',
      sortOrder: String(endpoint.sortOrder),
      relayAlias: endpoint.relayAlias ?? '',
    };
  }, [endpoint]);

  const [form, setForm] = useState<FormState>(initialState);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [probeResult, setProbeResult] = useState<{
    ok: boolean;
    models: string[];
    error?: string;
    blockedModels?: string[];
  } | null>(null);
  /** GCP 서비스계정 파일 업로드 UI 전용 상태 (스키마 전송 대상 아님). */
  const [gcpKeyInfo, setGcpKeyInfo] = useState<{ projectId: string; clientEmail: string } | null>(
    null,
  );
  const [gcpKeyError, setGcpKeyError] = useState<string | null>(null);
  // Location: 저장된 값이 큐레이션 목록 밖이면 '직접 입력' 모드로 시작.
  const initialLocation = isEdit ? (endpoint?.providerConfig.location ?? '') : '';
  const [locationCustom, setLocationCustom] = useState(
    initialLocation !== '' && !(VERTEX_REGIONS as readonly string[]).includes(initialLocation),
  );

  const providerMeta = PROVIDER_OPTIONS.find((p) => p.value === form.provider);
  const isCloudHosted = providerMeta?.cloudHosted ?? false;
  // Vertex 계열(GCP Gemini / GCP Claude) — service account JSON 업로드 + project/location UI 공유.
  const isVertex = isVertexProvider(form.provider);
  // Vertex 류는 cloudHosted 지만 SDK 가 자체 모델 목록 API 가 없어 자동 probe 안 함.
  const supportsAutoProbe = isCloudHosted && !isVertex;

  const createMut = useMutation({
    mutationFn: (body: CreateLlmEndpointRequest) => api.adminLlmCreate(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'llm-endpoints'] });
      props.onClose();
    },
    onError: (err: unknown) => {
      setServerError(err instanceof ApiError ? err.message : '생성에 실패했습니다.');
    },
  });

  const updateMut = useMutation({
    mutationFn: (body: UpdateLlmEndpointRequest) => api.adminLlmUpdate(endpoint?.id ?? '', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'llm-endpoints'] });
      props.onClose();
    },
    onError: (err: unknown) => {
      setServerError(err instanceof ApiError ? err.message : '수정에 실패했습니다.');
    },
  });

  const probeMut = useMutation({
    mutationFn: async () => {
      const extra = parseExtraHeaders(form.extraHeadersRaw);
      if (extra.error) throw new Error(extra.error);
      const useSavedKey = isEdit && form.apiKey.length === 0;
      const providerConfig = buildProviderConfig(form);
      return api.adminLlmProbeDraft({
        provider: form.provider,
        baseUrl: form.baseUrl,
        ...(useSavedKey ? { endpointId: endpoint?.id ?? '' } : { apiKey: form.apiKey }),
        extraHeaders: extra.value,
        ...(providerConfig ? { providerConfig } : {}),
      });
    },
    onSuccess: (r) => setProbeResult(r),
    onError: (err: unknown) => {
      setProbeResult({
        ok: false,
        models: [],
        error: err instanceof Error ? err.message : '연결 테스트 실패',
      });
    },
  });

  /**
   * cloudHosted provider (OpenAI / Anthropic / Gemini) 는 사용자가 API key 를 입력하고
   * 잠시 멈추면 자동으로 모델 목록을 가져온다 — preview / experimental 모델까지 실시간
   * 반영되도록 (예: Gemini 의 gemini-3.1-flash-lite-preview).
   *
   * - 편집 모드에서 키 미입력 (저장된 키 사용) 인 경우에도 자동 호출.
   * - 키 길이 ≥ 12 일 때만 호출 (오타로 인한 무의미한 호출 방지).
   * - 1초 debounce.
   * - 같은 (provider, baseUrl, apiKey) 조합으로는 한 번만 자동 호출.
   *
   * probeMut 는 매 렌더마다 새 객체라 deps 에 직접 넣으면 무한 호출. ref 로 latest
   * mutate 를 잡아두고 effect 안에서 호출.
   */
  const autoProbeRanFor = useRef<string | null>(null);
  const probeMutateRef = useRef(probeMut.mutate);
  probeMutateRef.current = probeMut.mutate;
  useEffect(() => {
    if (!supportsAutoProbe) return;
    const useSavedKey = isEdit && form.apiKey.length === 0;
    const apiKeyOk = useSavedKey || form.apiKey.length >= 12;
    if (!apiKeyOk) return;
    const sig = `${form.provider}|${form.baseUrl}|${useSavedKey ? '__saved__' : form.apiKey}`;
    if (autoProbeRanFor.current === sig) return;
    const handle = setTimeout(() => {
      autoProbeRanFor.current = sig;
      probeMutateRef.current();
    }, 1000);
    return () => clearTimeout(handle);
  }, [isCloudHosted, isEdit, form.provider, form.baseUrl, form.apiKey]);

  const onChangeProvider = (next: Provider) => {
    // Vertex ↔ 비Vertex 경계를 넘으면 입력 의미가 달라지는 apiKey 는 초기화
    // (붙여넣은 API 키 ↔ 업로드한 서비스계정 JSON). 같은 쪽이면 유지.
    // gcp ↔ gcp-anthropic (publisher 전환) 은 경계를 안 넘으므로 키·파일 요약 모두 보존.
    const crossesVertexBoundary = isVertexProvider(form.provider) !== isVertexProvider(next);
    setForm((f) => {
      const preset = PROVIDER_OPTIONS.find((p) => p.value === next);
      // 기존 baseUrl 가 다른 provider 의 프리셋이면 새 프리셋으로 교체, 아니면 유지.
      const wasPreset = PROVIDER_OPTIONS.some((p) => p.defaultBaseUrl === f.baseUrl);
      return {
        ...f,
        provider: next,
        baseUrl: wasPreset || f.baseUrl === '' ? (preset?.defaultBaseUrl ?? '') : f.baseUrl,
        apiKey: crossesVertexBoundary ? '' : f.apiKey,
        // extended thinking 미지원 provider 로 바뀌면 초기화.
        extendedThinking: providerSupportsExtendedThinking(next) ? f.extendedThinking : false,
      };
    });
    // 경계를 넘을 때만 파일 업로드 UI 상태 리셋 — publisher 전환(gcp↔gcp-anthropic)은 요약 보존.
    if (crossesVertexBoundary) {
      setGcpKeyInfo(null);
      setGcpKeyError(null);
    }
    // provider/publisher 가 바뀌면 모델 목록이 달라지므로 직전 probe 결과는 무효화.
    setProbeResult(null);
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setServerError(null);
    setErrors({});

    const extra = parseExtraHeaders(form.extraHeadersRaw);
    if (extra.error) {
      setErrors({ extraHeaders: extra.error });
      return;
    }
    const sortOrder = Number.parseInt(form.sortOrder, 10);
    if (Number.isNaN(sortOrder)) {
      setErrors({ sortOrder: '숫자를 입력하세요.' });
      return;
    }

    const aliasTrimmed = form.relayAlias.trim();

    const providerConfig = buildProviderConfig(form);

    if (!isEdit) {
      const body: CreateLlmEndpointRequest = {
        name: form.name,
        displayName: form.displayName.trim() || null,
        description: form.description.trim() || null,
        provider: form.provider,
        baseUrl: form.baseUrl,
        apiKey: form.apiKey,
        model: form.model,
        extraHeaders: extra.value,
        ...(providerConfig ? { providerConfig } : {}),
        isDefault: form.isDefault,
        isEnabledForUsers: form.isEnabledForUsers,
        extendedThinking: form.extendedThinking,
        supportsVision: form.supportsVision,
        isVisionDefault: form.isVisionDefault,
        supportsAudio: form.supportsAudio,
        isAudioDefault: form.isAudioDefault,
        supportsImageGeneration: form.supportsImageGeneration,
        isImageGenerationDefault: form.isImageGenerationDefault,
        supportsEmbedding: form.supportsEmbedding,
        embeddingDim: form.embeddingDim.trim().length > 0 ? Number(form.embeddingDim) : null,
        contextWindow: form.contextWindow.trim().length > 0 ? Number(form.contextWindow) : null,
        maxConcurrent: form.maxConcurrent.trim().length > 0 ? Number(form.maxConcurrent) : null,
        maxOutputTokens:
          form.maxOutputTokens.trim().length > 0 ? Number(form.maxOutputTokens) : null,
        sortOrder,
        relayAlias: aliasTrimmed || null,
      };
      const parsed = CreateLlmEndpointRequestSchema.safeParse(body);
      if (!parsed.success) {
        setErrors(zodIssuesToMap(parsed.error.issues));
        return;
      }
      createMut.mutate(parsed.data);
      return;
    }

    const body: UpdateLlmEndpointRequest = {
      name: form.name,
      displayName: form.displayName.trim() || null,
      description: form.description.trim() || null,
      provider: form.provider,
      baseUrl: form.baseUrl,
      model: form.model,
      extraHeaders: extra.value,
      ...(providerConfig ? { providerConfig } : {}),
      isDefault: form.isDefault,
      isEnabledForUsers: form.isEnabledForUsers,
      extendedThinking: form.extendedThinking,
      supportsVision: form.supportsVision,
      isVisionDefault: form.isVisionDefault,
      supportsAudio: form.supportsAudio,
      isAudioDefault: form.isAudioDefault,
      supportsImageGeneration: form.supportsImageGeneration,
      isImageGenerationDefault: form.isImageGenerationDefault,
      supportsEmbedding: form.supportsEmbedding,
      embeddingDim: form.embeddingDim.trim().length > 0 ? Number(form.embeddingDim) : null,
      contextWindow: form.contextWindow.trim().length > 0 ? Number(form.contextWindow) : null,
      maxConcurrent: form.maxConcurrent.trim().length > 0 ? Number(form.maxConcurrent) : null,
      maxOutputTokens: form.maxOutputTokens.trim().length > 0 ? Number(form.maxOutputTokens) : null,
      sortOrder,
      relayAlias: aliasTrimmed || null,
    };
    if (form.apiKey.length > 0) body.apiKey = form.apiKey;
    const parsed = UpdateLlmEndpointRequestSchema.safeParse(body);
    if (!parsed.success) {
      setErrors(zodIssuesToMap(parsed.error.issues));
      return;
    }
    updateMut.mutate(parsed.data);
  };

  const pending = createMut.isPending || updateMut.isPending;
  const title = isEdit ? '엔드포인트 수정' : '엔드포인트 추가';

  const footer = (
    <div className="flex justify-between gap-2">
      <Button
        type="button"
        variant="ghost"
        disabled={
          probeMut.isPending ||
          (!isCloudHosted && form.baseUrl.length === 0) ||
          (form.apiKey.length === 0 && !isEdit) ||
          (isVertex &&
            (form.gcpProjectId.trim().length === 0 || form.gcpLocation.trim().length === 0))
        }
        onClick={() => probeMut.mutate()}
      >
        {probeMut.isPending ? '테스트 중…' : '연결 테스트'}
      </Button>
      <div className="flex gap-2">
        <Button type="button" variant="ghost" onClick={props.onClose}>
          취소
        </Button>
        <Button type="submit" form="ep-endpoint-form" disabled={pending}>
          {pending ? '저장 중…' : '저장'}
        </Button>
      </div>
    </div>
  );

  return (
    <Modal
      open
      onClose={props.onClose}
      title={title}
      description={isEdit ? endpoint?.name : undefined}
      size="lg"
      footer={footer}
    >
      <form onSubmit={submit} id="ep-endpoint-form" className="space-y-4">
        <Field label="Provider" htmlFor="ep-provider" error={errors.provider}>
          <select
            id="ep-provider"
            // Vertex 계열(gcp/gcp-anthropic)은 드롭다운에서 'gcp' 한 항목으로 합쳐 표시.
            // 구체 publisher 는 아래 'Publisher' 셀렉트에서 고른다.
            value={isVertex ? 'gcp' : form.provider}
            onChange={(e) => onChangeProvider(ProviderSchema.parse(e.target.value))}
            className="h-10 w-full rounded-md border border-border bg-surface px-3 text-sm"
          >
            {PROVIDER_OPTIONS.filter((p) => p.value !== 'gcp-anthropic').map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field
            label="표시 이름"
            htmlFor="ep-display-name"
            hint="사용자 드롭다운에 노출되는 이름"
            error={errors.displayName}
          >
            <Input
              id="ep-display-name"
              value={form.displayName}
              onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
              placeholder="Opus 4.7"
            />
          </Field>
          <Field label="내부 이름" htmlFor="ep-name" hint="관리자 식별용" error={errors.name}>
            <Input
              id="ep-name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="anthropic-opus"
            />
          </Field>
        </div>

        <Field
          label="설명 (선택)"
          htmlFor="ep-description"
          hint="드롭다운 보조 텍스트"
          error={errors.description}
        >
          <Input
            id="ep-description"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            placeholder="고난도 작업에 가장 강력함"
          />
        </Field>

        {isVertex ? null : (
          <Field
            label={isCloudHosted ? 'Base URL (선택 — 비우면 SDK 기본값)' : 'Base URL'}
            htmlFor="ep-base-url"
            hint={
              isCloudHosted ? `비워도 동작. SDK 기본값: ${providerMeta?.defaultBaseUrl}` : undefined
            }
            error={errors.baseUrl}
          >
            <Input
              id="ep-base-url"
              value={form.baseUrl}
              onChange={(e) => setForm((f) => ({ ...f, baseUrl: e.target.value }))}
              placeholder={isCloudHosted ? '(비워두면 자동)' : 'https://...'}
            />
          </Field>
        )}

        {isVertex ? (
          <>
            <Field
              label="Publisher (모델 제공자)"
              htmlFor="ep-gcp-publisher"
              hint="사용할 모델을 만든 회사를 고르세요. Google 은 Gemini, Anthropic 은 Claude 모델을 제공합니다. 어느 쪽이든 아래 인증·프로젝트·리전 설정은 같습니다."
            >
              <select
                id="ep-gcp-publisher"
                value={form.provider}
                onChange={(e) => onChangeProvider(ProviderSchema.parse(e.target.value))}
                className="h-10 w-full rounded-md border border-border bg-surface px-3 text-sm"
              >
                <option value="gcp">Google (Gemini)</option>
                <option value="gcp-anthropic">Anthropic (Claude)</option>
              </select>
            </Field>

            <ServiceAccountJsonUpload
              id="ep-gcp-credentials"
              label={isEdit ? 'Service Account JSON (비우면 기존 값 유지)' : 'Service Account JSON'}
              error={errors.apiKey ?? gcpKeyError ?? undefined}
              placeholder={isEdit ? '기존 키 유지 (JSON 파일 선택…)' : 'JSON 파일 선택…'}
              onLoaded={(json, parsed) => {
                setForm((f) => ({
                  ...f,
                  apiKey: json,
                  // Vertex 의 project 는 SA 의 프로젝트와 일치해야 하므로 업로드 시 항상 덮어씀.
                  // (필드는 편집 가능 — 드문 cross-project 케이스는 업로드 후 수정.)
                  gcpProjectId: parsed.project_id,
                }));
                setGcpKeyInfo({ projectId: parsed.project_id, clientEmail: parsed.client_email });
                setGcpKeyError(null);
                setProbeResult(null);
              }}
              onError={(msg) => {
                setForm((f) => ({ ...f, apiKey: '' }));
                setGcpKeyInfo(null);
                setGcpKeyError(msg);
              }}
              summary={
                gcpKeyInfo ? (
                  <p className="mt-1 text-xs text-success">
                    ✓ {gcpKeyInfo.projectId} · {gcpKeyInfo.clientEmail}
                  </p>
                ) : isEdit ? (
                  <p className="mt-1 text-xs text-foreground-muted">
                    파일을 올리지 않으면 기존 키를 유지합니다.
                  </p>
                ) : null
              }
            />

            <GcpCredentialsGuide />

            <div className="grid grid-cols-2 gap-3">
              <Field
                label="GCP Project ID"
                htmlFor="ep-gcp-project"
                hint="Service Account JSON 업로드 후 자동 입력됩니다."
                error={errors.providerConfig ?? errors['providerConfig.projectId']}
              >
                <Input
                  id="ep-gcp-project"
                  value={form.gcpProjectId}
                  onChange={(e) => setForm((f) => ({ ...f, gcpProjectId: e.target.value }))}
                  placeholder="my-gcp-project"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                />
              </Field>
              <Field
                label="Location (Region)"
                htmlFor="ep-gcp-location"
                hint="모델을 사용할 리전을 선택하세요. 목록에 없으면 '직접 입력'을 골라 입력할 수 있습니다."
                error={errors['providerConfig.location']}
              >
                <select
                  id="ep-gcp-location"
                  value={locationCustom ? '__custom__' : form.gcpLocation}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '__custom__') {
                      setLocationCustom(true);
                      setForm((f) => ({ ...f, gcpLocation: '' }));
                    } else {
                      setLocationCustom(false);
                      setForm((f) => ({ ...f, gcpLocation: v }));
                    }
                  }}
                  className="h-10 w-full rounded-md border border-border bg-surface px-3 text-sm"
                >
                  {VERTEX_REGIONS.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                  <option value="__custom__">직접 입력…</option>
                </select>
                {locationCustom ? (
                  <Input
                    id="ep-gcp-location-custom"
                    className="mt-2"
                    value={form.gcpLocation}
                    onChange={(e) => setForm((f) => ({ ...f, gcpLocation: e.target.value }))}
                    placeholder="예: us-west8"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                  />
                ) : null}
              </Field>
            </div>
          </>
        ) : (
          <Field
            label={isEdit ? 'API 키 (비우면 기존 값 유지)' : 'API 키'}
            htmlFor="ep-api-key"
            error={errors.apiKey}
          >
            <PasswordInput
              id="ep-api-key"
              value={form.apiKey}
              onChange={(e) => setForm((f) => ({ ...f, apiKey: e.target.value }))}
              autoComplete="off"
              placeholder={isEdit ? '(기존 키 유지)' : 'sk-...'}
            />
          </Field>
        )}

        <Field
          label="모델 ID"
          htmlFor="ep-model"
          hint={
            providerMeta?.recommendedModels && probeResult?.ok !== true
              ? '아래 권장 모델에서 선택하거나, 연결 테스트 후 발견된 모델에서 선택'
              : "모르면 위 '연결 테스트' 후 발견된 모델에서 선택"
          }
          error={errors.model}
        >
          <Input
            id="ep-model"
            value={form.model}
            onChange={(e) => setForm((f) => ({ ...f, model: e.target.value }))}
            // provider/publisher 의 권장 모델 첫 항목을 예시로 — 선택에 따라 자동 변경.
            placeholder={providerMeta?.recommendedModels?.[0] ?? 'gemma-3-27b'}
          />
          {form.model && isBlockedModel(form.model) ? (
            <p className="mt-1 text-[11px] text-destructive">{BLOCKED_MODEL_MESSAGE}</p>
          ) : null}
          {probeResult?.ok && probeResult.models.length > 0 ? (
            <div className="mt-2 space-y-1">
              <div className="text-[11px] text-foreground-muted">
                발견된 모델 {probeResult.models.length}개 — 선택하면 위 입력에 채워집니다.
              </div>
              {probeResult.blockedModels && probeResult.blockedModels.length > 0 ? (
                <div className="text-[11px] text-destructive">
                  Llama-4 계열 ({probeResult.blockedModels.join(', ')}) 은 등록할 수 없습니다.
                </div>
              ) : null}
              <select
                value=""
                onChange={(e) => {
                  const v = e.target.value;
                  if (v && !isBlockedModel(v)) setForm((f) => ({ ...f, model: v }));
                }}
                className="h-10 w-full rounded-md border border-border bg-surface px-3 text-sm"
              >
                <option value="">발견된 모델에서 선택…</option>
                {[...probeResult.models]
                  .sort((a, b) => a.localeCompare(b))
                  .map((m) =>
                    isBlockedModel(m) ? (
                      <option key={m} value={m} disabled>
                        {m} — 등록 불가
                      </option>
                    ) : (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ),
                  )}
              </select>
            </div>
          ) : providerMeta?.recommendedModels && providerMeta.recommendedModels.length > 0 ? (
            <div className="mt-2 space-y-1">
              <div className="text-[11px] text-foreground-muted">
                권장 모델 — 선택하면 위 입력에 채워집니다. 정확한 ID 는 연결 테스트로 확인하세요.
              </div>
              <select
                value=""
                onChange={(e) => {
                  const v = e.target.value;
                  if (v) setForm((f) => ({ ...f, model: v }));
                }}
                className="h-10 w-full rounded-md border border-border bg-surface px-3 text-sm"
              >
                <option value="">권장 모델에서 선택…</option>
                {providerMeta.recommendedModels.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
        </Field>

        <Field
          label="Extra headers (JSON, 선택)"
          htmlFor="ep-extra-headers"
          hint='예: {"X-Custom": "value"}'
          error={errors.extraHeaders}
        >
          <textarea
            id="ep-extra-headers"
            value={form.extraHeadersRaw}
            onChange={(e) => setForm((f) => ({ ...f, extraHeadersRaw: e.target.value }))}
            rows={3}
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm font-mono"
            placeholder="{}"
          />
        </Field>

        <Field
          label="컨텍스트 윈도우 (토큰)"
          htmlFor="ep-context-window"
          hint="모델이 한 번에 처리 가능한 토큰 한도. 챗 turn 시작 시 추정 토큰 / 이 값 비율로 헤더 chip + 임계치 경고. 비우면 32000 (Gemma) fallback. 예: Gemma 31B=32768, Claude 3.5=200000, GPT-4o=128000"
          error={errors.contextWindow}
        >
          <Input
            id="ep-context-window"
            type="number"
            value={form.contextWindow}
            onChange={(e) => setForm((f) => ({ ...f, contextWindow: e.target.value }))}
            placeholder="32768"
          />
        </Field>

        <Field
          label="동시 요청 상한"
          htmlFor="ep-max-concurrent"
          hint="이 엔드포인트(GPU)로 동시에 처리할 in-flight 요청 수. 초과 요청은 슬롯이 빌 때까지 대기('LLM 리소스 할당 대기중'). 사용자 챗은 (값-1)까지만 쓰고 1개는 시스템 운영(분류기/헬스체크 등)용으로 예약합니다. 비우면 무제한. 기본 10."
          error={errors.maxConcurrent}
        >
          <Input
            id="ep-max-concurrent"
            type="number"
            min={1}
            value={form.maxConcurrent}
            onChange={(e) => setForm((f) => ({ ...f, maxConcurrent: e.target.value }))}
            placeholder="10"
          />
        </Field>

        <Field
          label="최대 출력 토큰"
          htmlFor="ep-max-output-tokens"
          hint="한 응답에서 모델이 생성할 수 있는 최대 출력(completion) 토큰. 답변이 중간에 잘리면 늘리세요. 비우면 8192(Anthropic·OpenAI 호환) 또는 모델 기본(Gemini/Vertex)을 사용합니다. provider 별로 알아서 적용됩니다. 256~200000."
          error={errors.maxOutputTokens}
        >
          <Input
            id="ep-max-output-tokens"
            type="number"
            min={256}
            value={form.maxOutputTokens}
            onChange={(e) => setForm((f) => ({ ...f, maxOutputTokens: e.target.value }))}
            placeholder="8192"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Sort order" htmlFor="ep-sort-order" error={errors.sortOrder}>
            <Input
              id="ep-sort-order"
              type="number"
              value={form.sortOrder}
              onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))}
            />
          </Field>
          <Field
            label="API 릴레이 표시 이름"
            htmlFor="ep-relay-alias"
            hint="외부에서 model 필드에 넣을 값. 예: gemma4, gemma4:31b. 본딩 alias 와 통틀어 unique. 비우면 릴레이 미노출."
            error={errors.relayAlias}
          >
            <Input
              id="ep-relay-alias"
              value={form.relayAlias}
              onChange={(e) => setForm((f) => ({ ...f, relayAlias: e.target.value }))}
              placeholder="gemma4:26b"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
            />
          </Field>
        </div>

        <div className="space-y-2 pt-1">
          <Checkbox
            id="ep-is-default"
            checked={form.isDefault}
            onChange={(v) => setForm((f) => ({ ...f, isDefault: v }))}
            label="기본 모델로 설정 (시스템 전체 1개)"
          />
          <Checkbox
            id="ep-enabled-users"
            checked={form.isEnabledForUsers}
            onChange={(v) => setForm((f) => ({ ...f, isEnabledForUsers: v }))}
            label="사용자 드롭다운에 노출"
          />
          <Checkbox
            id="ep-extended-thinking"
            checked={form.extendedThinking}
            disabled={!providerSupportsExtendedThinking(form.provider)}
            onChange={(v) => setForm((f) => ({ ...f, extendedThinking: v }))}
            label="적응형 사고 기본 ON (Claude 계열 전용 — 직접 Anthropic / Vertex Claude)"
          />
          <Checkbox
            id="ep-supports-vision"
            checked={form.supportsVision}
            onChange={(v) =>
              setForm((f) => ({
                ...f,
                supportsVision: v,
                // supports off → vision default 도 자동 off.
                isVisionDefault: v ? f.isVisionDefault : false,
              }))
            }
            label="Vision 지원 (이미지 입력 가능 — wiki 자료 이미지 캡션에 활용)"
          />
          <Checkbox
            id="ep-is-vision-default"
            checked={form.isVisionDefault}
            disabled={!form.supportsVision}
            onChange={(v) => setForm((f) => ({ ...f, isVisionDefault: v }))}
            label="기본 Vision 모델로 설정 (시스템 전체 1개 — wiki ingest 등에서 자동 사용)"
          />
          <Checkbox
            id="ep-supports-audio"
            checked={form.supportsAudio}
            onChange={(v) =>
              setForm((f) => ({
                ...f,
                supportsAudio: v,
                // supports off → audio default 도 자동 off.
                isAudioDefault: v ? f.isAudioDefault : false,
              }))
            }
            label="Audio 지원 (음성 입력 가능 — gemma4:e4b 등)"
          />
          <Checkbox
            id="ep-is-audio-default"
            checked={form.isAudioDefault}
            disabled={!form.supportsAudio}
            onChange={(v) => setForm((f) => ({ ...f, isAudioDefault: v }))}
            label="기본 Audio 모델로 설정 (시스템 전체 1개)"
          />
          <Checkbox
            id="ep-supports-image-generation"
            checked={form.supportsImageGeneration}
            onChange={(v) =>
              setForm((f) => ({
                ...f,
                supportsImageGeneration: v,
                // supports off → image default 도 자동 off.
                isImageGenerationDefault: v ? f.isImageGenerationDefault : false,
              }))
            }
            label="이미지 생성 지원 (Gemini Nano Banana 등 — 이미지 생성 전용 모델)"
          />
          <Checkbox
            id="ep-is-image-generation-default"
            checked={form.isImageGenerationDefault}
            disabled={!form.supportsImageGeneration}
            onChange={(v) => setForm((f) => ({ ...f, isImageGenerationDefault: v }))}
            label="기본 이미지 생성 모델로 설정 (시스템 전체 1개 — image.generate 가 자동 사용)"
          />
          <Checkbox
            id="ep-supports-embedding"
            checked={form.supportsEmbedding}
            onChange={(v) =>
              setForm((f) => ({
                ...f,
                supportsEmbedding: v,
                embeddingDim: v ? f.embeddingDim : '',
              }))
            }
            label="Embedding 지원 (/v1/embeddings — wiki 페이지 의미 검색)"
          />
          {form.supportsEmbedding ? (
            <Field
              label="Embedding Dimension"
              htmlFor="ep-embedding-dim"
              hint="모델의 임베딩 차원. BGE-M3 = 1024, OpenAI text-embedding-3-small = 1536, Qwen3-Embedding-0.6B = 1024"
            >
              <Input
                id="ep-embedding-dim"
                type="number"
                min={1}
                max={8192}
                value={form.embeddingDim}
                onChange={(e) => setForm((f) => ({ ...f, embeddingDim: e.currentTarget.value }))}
                placeholder="1024"
              />
            </Field>
          ) : null}
        </div>

        {probeResult ? (
          <div
            className={
              probeResult.ok
                ? 'rounded-md border border-success/30 bg-success/5 px-3 py-2 text-sm text-success'
                : 'rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive'
            }
          >
            {probeResult.ok
              ? `연결 성공 — 모델 ${probeResult.models.length}개 발견`
              : `연결 실패: ${probeResult.error ?? '알 수 없는 오류'}`}
          </div>
        ) : null}

        {serverError ? <p className="text-sm text-destructive">{serverError}</p> : null}
      </form>
    </Modal>
  );
}

function Checkbox({
  id,
  checked,
  onChange,
  label,
  disabled,
}: {
  id: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <label htmlFor={id} className="flex items-center gap-2 text-sm select-none">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        className="size-4"
      />
      <span className={disabled ? 'text-foreground-muted' : ''}>{label}</span>
    </label>
  );
}

/**
 * 폼의 GCP 필드 → ProviderConfig 객체. 비어있으면 undefined (서버는 omit 시 기존 유지).
 * Vertex 계열(gcp / gcp-anthropic)이 아니면 stale 값이 남아도 무시하도록 provider 가드.
 */
function buildProviderConfig(form: FormState): ProviderConfig | undefined {
  if (!isVertexProvider(form.provider)) return undefined;
  const projectId = form.gcpProjectId.trim();
  const location = form.gcpLocation.trim();
  if (!projectId && !location) return undefined;
  return {
    ...(projectId ? { projectId } : {}),
    ...(location ? { location } : {}),
  };
}

function parseExtraHeaders(
  raw: string,
):
  | { value: Record<string, string> | undefined; error: null }
  | { error: string; value: undefined } {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return { value: undefined, error: null };
  try {
    const parsed = JSON.parse(trimmed);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      return { error: '객체 형식이어야 합니다.', value: undefined };
    }
    const entries = Object.entries(parsed);
    if (!entries.every(([, v]) => typeof v === 'string')) {
      return { error: '값은 모두 문자열이어야 합니다.', value: undefined };
    }
    return { value: parsed as Record<string, string>, error: null };
  } catch {
    return { error: 'JSON 파싱 실패', value: undefined };
  }
}

function zodIssuesToMap(
  issues: ReadonlyArray<{ path: (string | number)[]; message: string }>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const issue of issues) {
    if (issue.path.length === 0) continue;
    // 단일 키 ("apiKey") 와 nested ("providerConfig.projectId") 둘 다 처리.
    const flatKey = issue.path.map(String).join('.');
    const topKey = String(issue.path[0]);
    if (!(flatKey in out)) out[flatKey] = issue.message;
    if (!(topKey in out)) out[topKey] = issue.message;
  }
  return out;
}

function GcpCredentialsGuide() {
  return (
    <details className="rounded-md border border-border bg-surface-2 text-sm">
      <summary className="cursor-pointer select-none px-3 py-2 text-foreground-muted">
        Service Account JSON 은 어떻게 발급하나요?
      </summary>
      <div className="space-y-2 border-t border-border px-3 py-3 text-xs leading-relaxed">
        <ol className="list-decimal space-y-1 pl-5">
          <li>
            GCP Cloud Console → <strong>IAM & Admin → Service Accounts</strong> 진입
          </li>
          <li>
            <strong>Create Service Account</strong> 클릭, 이름/설명 입력 후 생성
          </li>
          <li>
            <strong>Grant access</strong> 단계에서 역할로 <code>Vertex AI User</code> 추가
          </li>
          <li>
            생성된 서비스 계정 행 클릭 → 상단 <strong>Keys</strong> 탭
          </li>
          <li>
            <strong>Add Key → Create new key → JSON</strong> → 파일 다운로드
          </li>
          <li>다운로드된 JSON 키 파일을 위 입력란에 그대로 업로드</li>
        </ol>
        <p className="pt-1 text-foreground-muted">
          ⚠️ 다운로드한 JSON 키 파일은 시크릿 — 저장 후 로컬 사본 삭제 권장. innermate 는 받은 내용을
          INNERMATE_SECRET_KEY 로 AES-256-GCM 암호화해 DB 에 저장합니다.
        </p>
      </div>
    </details>
  );
}
